import { BALANCE } from '../balance.ts';
import { RULES_VERSION } from '../version.ts';
import { getMap } from '../maps/graph.ts';
import { Rng } from '../rng.ts';
import {
  ROLES_BY_PLAYER_COUNT,
  type GameConfig,
  type GameState,
  type PlayerState,
  type Role,
} from '../types.ts';
import { emit, type Ctx } from './context.ts';

/** Problems with a configuration; empty means it is valid. */
export function validateConfig(config: GameConfig): string[] {
  const problems: string[] = [];
  const n = config.players.length;
  if (n < BALANCE.minPlayers || n > BALANCE.maxPlayers) {
    problems.push(`${n} players: need ${BALANCE.minPlayers}–${BALANCE.maxPlayers}`);
  }
  const ids = new Set(config.players.map((p) => p.id));
  if (ids.size !== n) problems.push('duplicate player ids');
  if (config.players.some((p) => p.id.trim() === '')) problems.push('empty player id');
  if (!['bank', 'museum', 'villa', 'express'].includes(config.mapId)) problems.push('unknown map');
  if (!['easy', 'normal', 'pro', 'legend'].includes(config.difficulty)) {
    problems.push('unknown difficulty');
  }
  if (
    config.timerMs !== undefined &&
    (!Number.isInteger(config.timerMs) || config.timerMs < 60_000)
  ) {
    problems.push('timer must be an integer of at least 60 000 ms');
  }
  if (config.infiltratorEligible) {
    const known = config.infiltratorEligible.every((id) => ids.has(id));
    if (!known || config.infiltratorEligible.length === 0) {
      problems.push('infiltratorEligible must name players of this game');
    }
  }
  if (config.roles) {
    const wanted = ROLES_BY_PLAYER_COUNT[n];
    const given = config.players.map((p) => config.roles?.[p.id]);
    if (!wanted || given.some((r) => r === undefined))
      problems.push('roles missing for some players');
    else if ([...given].sort().join() !== [...wanted].sort().join()) {
      problems.push('roles are not a permutation of the roles for this player count');
    }
  }
  return problems;
}

/**
 * Starts a game in the briefing. Everything random (roles, infiltrator, puzzles, events) comes
 * from `seed`, so the same seed and config always give the same game.
 */
export function createGame(config: GameConfig, seed: string): GameState {
  const problems = validateConfig(config);
  if (problems.length > 0) throw new RangeError(`invalid game config: ${problems.join('; ')}`);
  getMap(config.mapId);

  const n = config.players.length;
  const roleRng = Rng.create(seed).fork('roles');
  const roles: readonly Role[] = roleRng.shuffle(ROLES_BY_PLAYER_COUNT[n] as readonly Role[]);

  const players: PlayerState[] = config.players.map((p, seat) => {
    const role = config.roles?.[p.id] ?? roles[seat];
    if (!role) throw new Error('role assignment failed');
    return {
      id: p.id,
      name: p.name,
      role,
      seat,
      expelled: false,
      pin: null,
      powers: {
        planB: role === 'mastermind' ? BALANCE.planBCharges : 0,
        loops: role === 'hacker' ? BALANCE.loopCharges : 0,
        coins: role === 'lookout' ? BALANCE.coins : 0,
        petards: role === 'lookout' ? BALANCE.petards : 0,
      },
    };
  });

  const infRng = Rng.create(seed).fork('infiltrator');
  const chance = BALANCE.infiltratorChancePermille[n] ?? 0;
  const present =
    config.infiltrator === 'always' || (config.infiltrator === 'normal' && infRng.chance(chance));
  const eligible = config.infiltratorEligible
    ? players.filter((p) => config.infiltratorEligible?.includes(p.id))
    : players;
  const infiltrator = present ? infRng.pick(eligible).id : null;

  const s: GameState = {
    rulesVersion: RULES_VERSION,
    config: structuredClone(config),
    seed,
    clockMs: 0,
    phase: 'briefing',
    phaseStartedMs: 0,
    nextEventId: 1,
    nextActionSeq: 1,
    players,
    infiltrator: { id: infiltrator, available: { tipoff: true, tamper: true, rush: true } },
    ready: [],
    planning: {
      substage: 'awaiting',
      stageStartedMs: 0,
      rejections: 0,
      proposal: null,
      votes: {},
      approved: null,
      decidedAlone: false,
    },
    alarm: 0,
    exec: null,
    hideout: null,
    result: null,
    log: [],
  };
  const ctx: Ctx = { s, events: [] };
  emit(ctx, 'phase', { phase: 'briefing' });
  return s;
}
